// BRK-S141 — the bid result's rules, as this portal needs them to EXPLAIN the
// one the server enforces.
//
// ── ⚠️ A MIRROR, NOT AN AUTHORITY ───────────────────────────────────────────
//
// Discovery holds the order's submitted bid and enforces the threshold. PMS
// deliberately does not pre-check it either and passes discovery's 422 through,
// for a reason worth repeating here: a rule enforced in two places is a rule
// that will disagree with itself, and it would disagree in the direction that
// matters — a client refusing a save the service would have accepted, with the
// broker unable to tell which side was wrong.
//
// So everything here exists to let the panel say WHY the explanation box turned
// required before the round-trip, and to keep it from demanding one the server
// would not. When the two disagree, the SERVER wins: the 422 is rendered inline
// and the local hint is never a gate the broker cannot get past.
//
// PARITY, NOT REUSE — the rule this file mirrors is
// `pms-local lib/bond-bid-result.ts`, itself a mirror of surety's
// `src/bid-result.ts` (SUR-S152). The three repos share no code, so the
// agreement is pinned by `test/lib/bidResult.test.ts` against the same worked
// figures rather than by a dependency.

/**
 * Where OUR BID is read from, in order of preference. Mirrors upstream's
 * `OUR_BID_KEYS`.
 *
 * ⚠️ THIS IS THE OPPOSITE PREFERENCE FROM THE PENAL SUM'S, and the two answer
 * different questions. The penal sum is what the BOND is for, so it prefers
 * `bondAmount`. This wants what the contractor bid FOR THE WORK — the contract
 * price — and the penal sum is only a fallback, because on a bid bond it is a
 * percentage of the price rather than the price itself.
 *
 * The consequence decides the 10% rule: an order carrying only a penal sum has
 * its gap computed against a figure that is not really its bid. That is the
 * honest best available, and the reference Construction configuration makes
 * `estimatedContractPrice` REQUIRED so an order filed through it always carries
 * the right one.
 */
export const OUR_BID_KEYS: readonly string[] = [
  "estimatedContractPrice",
  "bondAmount",
];

/**
 * Our bid as the order carries it, or **null** when it carries no usable
 * amount.
 *
 * ONLY A FINITE NUMBER COUNTS. A formatted string is refused rather than parsed
 * — D-10's display/wire split runs both ways, and a currency value is a number
 * on the wire, so a string here is a client that wrote around the validator.
 *
 * NULL rather than 0: a defaulted zero would make every loss look like a bid
 * infinitely below the field, which is the one answer that demands an
 * explanation nobody can give.
 */
export function ourBidFromOrder(
  data: Readonly<Record<string, unknown>> | null | undefined,
): number | null {
  if (!data) return null;
  for (const key of OUR_BID_KEYS) {
    const value = data[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

/**
 * Greg's threshold, as a PERCENTAGE.
 *
 * ⚠️ `10`, not `0.10` — the gap it is compared against is a percentage too
 * (`12.5` means 12.5%), which is what the contract's `details.gapPercent` key
 * publishes and what the panel renders. Named rather than inlined because it
 * appears in the copy as well as in the comparison, and two spellings of a
 * threshold is how a sentence stops describing the rule.
 */
export const THRESHOLD_PERCENT = 10;

/**
 * How far under the next lowest bid ours came in, as a PERCENTAGE rounded to
 * two places — for DISPLAY.
 *
 * The comparison below is against the unrounded value, so a 10.004% gap is over
 * the line and reads as `10`. A negative result is a bid ABOVE the next lowest,
 * which is a real thing to render (they lost on something other than price).
 */
export function bidGapPercent(ourBid: number, nextLowestBid: number): number {
  return (
    Math.round(((nextLowestBid - ourBid) / nextLowestBid) * 100 * 100) / 100
  );
}

/**
 * Is the gap past the threshold? STRICTLY greater, so exactly 10% is not — an
 * inclusive comparison would demand an explanation for a bid that is merely
 * competitive.
 *
 * ⚠️ COMPARED WITHOUT DIVIDING, on purpose, and this is the one line in the
 * file that is a bug fix rather than a transcription. `(100000 - 90000) /
 * 100000` is a clean 10% on paper and a binary float can land either side of a
 * comparison written `> 10`; discovery computes it in exact decimal, so a
 * mirror using doubles would demand an explanation the live service does not
 * (or waive one it does) on the width of a rounding error. Cross-multiplying
 * keeps both sides in the integers money is actually made of.
 */
export function isPastThreshold(
  ourBid: number,
  nextLowestBid: number,
): boolean {
  return (nextLowestBid - ourBid) * 100 > nextLowestBid * THRESHOLD_PERCENT;
}

export interface BidExplanationCheck {
  outcome: "won" | "lost";
  /** From the ORDER, never from the form — see `ourBidFromOrder`. */
  ourBidAmount: number | null | undefined;
  nextLowestBidAmount: number | null | undefined;
  explanation: string | null | undefined;
}

/**
 * Is an explanation required, and still missing?
 *
 * Five conditions, and every one of them is a reason NOT to demand one:
 *
 *   • the bid was WON — the rule is about a job we lost and priced oddly, not
 *     about winning cheaply, however far under we came in;
 *   • an explanation has already been given;
 *   • we do not know OUR OWN bid. That is a gap in OUR data rather than in the
 *     broker's answer, so it must not refuse their record — upstream is
 *     explicit about this, and it is the condition a client is most likely to
 *     get wrong, because the natural instinct is to treat a missing figure as
 *     the strictest case;
 *   • we do not know the next lowest — a ratio needs two figures;
 *   • the next lowest is not positive: a zero next-lowest bid is not a bid, and
 *     the ratio computed from one is meaningless.
 */
export function bidExplanationRequired(check: BidExplanationCheck): boolean {
  if (check.outcome !== "lost") return false;
  if (
    check.explanation !== undefined &&
    check.explanation !== null &&
    check.explanation.trim() !== ""
  ) {
    return false;
  }
  const ours = check.ourBidAmount;
  const theirs = check.nextLowestBidAmount;
  if (typeof ours !== "number" || typeof theirs !== "number") return false;
  if (!(theirs > 0)) return false;
  return isPastThreshold(ours, theirs);
}
